<!--
 * @Author: Victor
 * @Date: 2023-12-27 19:08:08
 * @LastEditors: Merlin
 * @LastEditTime: 2025-06-20 11:51:15
 * @description: 组件名： <CasinoBetOptionSectionAmountOption 
    金额输入框做一下调整
    1.点击输入框弹出悬浮框，金额后台可调整。
    2.点击自定义筹码拉起数字键盘
    3.双击输入框直接拉起数字键盘
-->
<template>
    <div class="input-wrap border rounded-[10px] h-full border-[#4b5b74] bg-[#161f2c] p-2.5">
        <!-- 金額輸入以及顯示 -->
        <div class="input-coat col column items-center justify-center">
            <div class="flex text-xl w-auto font-bold content-center items-center gap-1 flex-nowrap">
                <CurrentCoin class="w-[24px]" :name="currency"/>
                <input
                    inputmode="decimal"
                    type="number"
                    :min="0"
                    :max="maxValue"
                    :value="amount"
                    _input="onInputBetAmount"
                    @keydown="onlyNumberPress02"
                    @blur="onInputBlurChangeBetAmount"
                    class=" bg-[#161F2CFF] w-[98px] outline-none h-[24px]"
                    :disabled="isDisabled" 
                    pattern="^[0-9]{1,9}([.][0-9]{1,8})?$"
                    :ref="el => oAmountInput=el"
                />
            </div>
            <!-- ↓ 金额选择器（浮动气泡） -->
            <ChipList
                v-model="amount"
                :disabled="isDisabled"
                :amountSetter="updateAmount"
                :input="oAmountInput"
            />
        </div>
        <!-- ↑ 上方输入框 -->
        <!-- ◆ 上下分界 ◆◆◆◆◆◆◆◆◆◆◆◆◆◆◆ --> 
        <!-- ↓ 下方按钮 --> 
        <div class="btn-group flex gap-[5px] mt-auto">
            <!-- 最大下注金额 -->
            <Button
                type="button"
                v-if="gameName != 'rouletteGame'"
                :sound="sound"
                class="flex-1 h-[26px] !rounded-[7px] leading-6 button-gray !p-0 text-xs rounded-borders cursor-pointer focus:text-white"
                :class="[!isSubmiting ? '' : 'disabled']"
                @click="!isSubmiting && onMaxBetAmountClick()"
                :disabled="isSubmiting"
            >
                {{ t.Max }}
            </Button>
            <!-- 金額對半 -->
            <Button
                type="button"
                :sound="sound"
                class="flex-1 h-[26px] !rounded-[7px] leading-6 button-gray !p-0 text-xs cursor-pointer focus:text-white"
                :class="[!isSubmiting ? '' : 'disabled']"
                @click="!isSubmiting && onHalfBetAmountClick()"
                :disabled="isSubmiting"
            >
                1/2
            </Button>
            <!-- 金額翻倍 -->
            <Button
                type="button"
                :sound="sound"
                class="flex-1 h-[26px] !rounded-[7px] leading-6 button-gray !p-0 text-xs cursor-pointer focus:text-white"
                :class="[!isSubmiting ? '' : 'disabled']"
                @click="!isSubmiting && onDoubleBetAmountClick()"
                :disabled="isSubmiting"
            >
                2x
            </Button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import useTranslation from '~/i18n/useTransLation';
import betHelper from '~/common/js/betHelper';
import ChipList from './chipList.vue';

const isDev = import.meta.dev;
const t = useTranslation();
const userStore = useUserStore();
const props = defineProps({
    betAmountModalValue: {
        type: [Number, String],
        required: true,
    },
    isSubmiting: {
        // 用來disable按鈕
        type: Boolean,
        required: true,
    },
    sound: {
        type: Boolean,
        default: void 0, // 这里不使用 false 做为默认，以便于识别是否有传入值 
    },
    gameName: {
        type: String,
        default: '',
    },
    changeAmount: {
        type: Function, // roulette 专用 
    },
    currency: {
        type: String, // crash 专用 
        default: '',
    },
});


// ↓ 下注金额上限 
const maxValue = computed(() => {
    return betHelper.onMaxClick({
        currentBalance: userStore.oNowCurrency.balance,
        maxBetAmount: betHelper.getMaxBetAmout(),
    })[0];
});

const isDisabled = computed(()=>{
    if (props.gameName == 'rouletteGame'){
        return true; // 禁用
    }
    return props.isSubmiting;
});


// ↓ 重要值 ◆ 下注金额 
const amount = ref('0');
const oAmountInput = ref(null);

const emit = defineEmits(['update:betAmountModalValue']);

// ↓ 接收一个代表赌金的值，赋值 
const updateAmount = (value) => {
    amount.value = getFixed(value, 2); // 按最新规则，只保留2位小数   
    // ↑ 先执行（此处赋值大概率会“立即”被上层的下发值覆盖）
    // ↓ 报告新值，上级收到值后多数情况会重新下发这个值到此组件
    // ↓ 且此下发动作将再次为 amount.value 赋值 
    emit(
        'update:betAmountModalValue',
        Number(amount.value),
    );
};

const onHalfBetAmountClick = () => {
    if (props.gameName == 'rouletteGame'){
        props.changeAmount('half');
        return;
    }
    const [amount, toast] = betHelper.onHalfClick(props.betAmountModalValue);
    showToastIfNeeded(toast);
    updateAmount(amount);
};

const onDoubleBetAmountClick = () => {
    if (props.gameName == 'rouletteGame'){
        props.changeAmount('double');
        return;
    }
    const [result, toast] = betHelper.onDoubleClick({
        currentBalance: userStore.oNowCurrency.balance,
        maxBetAmount: betHelper.getMaxBetAmout(),
        betAmount: props.betAmountModalValue,
    });
    showToastIfNeeded(toast);
    // ↓ 若不用这个 getFixed() 则会发生 1.999 被转为 2.00 的情况，并在下注时报错
    const sResult = getFixed(result, 2);
    updateAmount(sResult);
};

const onMaxBetAmountClick = () => {
    const [result, toast] = betHelper.onMaxClick({
        currentBalance: userStore.oNowCurrency.balance,
        maxBetAmount: betHelper.getMaxBetAmout(),
    });
    showToastIfNeeded(toast);
    // ↓ 若不用这个 getFixed() 则会发生 1.999 被转为 2.00 的情况，并在下注时报错
    const sResult = getFixed(result, 2);
    updateAmount(sResult);
};

// ↓ 输入框 input 事件（旧方法，暂时停用） 
const onInputBetAmount = (ev: any) => {
    if (ev.target.value.length > 9) {
        ev.target.value = ev.target.value.slice(0, 10);
        return;
    }
    const [maxValue, toast] = betHelper.onMaxClick({
        currentBalance: userStore.oNowCurrency.balance,
        maxBetAmount: betHelper.getMaxBetAmout(),
    });
    showToastIfNeeded(toast);
    if (Number(ev.target.value) >= maxValue) {
        const result = maxValue.toString().slice(0, 10);
        updateAmount(Number(result));
        ev.target.value = result;
        amount.value = result;
        return;
    }
    updateAmount(Number(ev.target.value));
    amount.value = ev.target.value;
};

// ↓输入框 blur 事件 
const onInputBlurChangeBetAmount = (ev: any) => {
    const [sFinalValue, toast] = betHelper.fixAmountValue(
        ev.target.value,
    );
    showToastIfNeeded(toast);
    isDev && console.log('输入框 blur 已触发，输入值与上报的结果值：', ev.target.value, sFinalValue);
    ev.target.value = sFinalValue;
    updateAmount(sFinalValue);
};


// 拿取匯率後更新
watch(
    () => props.betAmountModalValue,
    (val) => {
        const sFinalValue = getFixed(val, 2);
        isDev && console.log('下注金额，下发值与转换值：', val, sFinalValue);  
        amount.value = sFinalValue;
    },
    { immediate: true, deep: true },
);
</script>


<style scoped lang="scss">

.input-wrap{
    display: flex;
    flex-direction: column;
}
.button-gray:focus{
    background: linear-gradient(1turn, #0c5eff 0.8%, #1398f9);
    box-shadow: 0 3px 28px rgba(34, 131, 246, 0.4), inset 0 3px 3px rgba(255, 255, 255, 0.3019607843);
}
</style>
